import React, { useState, useContext, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';
import i18n from '../i18n/config';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const { user, logout, getDashboardPath } = useContext(AuthContext);
  const { language, setLanguage } = useLanguageCurrency();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const profileMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && isMenuOpen) {
        const menuButton = event.target.closest('button[aria-label="Toggle menu"]');
        if (!menuButton) {
          setIsMenuOpen(false);
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);


  const handleLogout = () => {
    logout();
    setIsProfileMenuOpen(false);
    navigate('/');
  };

  const handleDashboardClick = () => {
    setIsProfileMenuOpen(false);
    navigate(getDashboardPath());
  };

  const isActive = (path) => location.pathname === path;

  // Light enforcement: run once after mount and on scroll/resize only (no RAF/interval to avoid blocking main thread)
  useEffect(() => {
    const enforceFixed = () => {
      try {
        const navbar = document.getElementById('main-navbar');
        if (!navbar) return;
        navbar.style.setProperty('position', 'fixed', 'important');
        navbar.style.setProperty('top', '0', 'important');
        navbar.style.setProperty('left', '0', 'important');
        navbar.style.setProperty('right', '0', 'important');
        navbar.style.setProperty('width', '100%', 'important');
        navbar.style.setProperty('z-index', '9999', 'important');
      } catch (e) {
        // silent
      }
    };

    const t = setTimeout(enforceFixed, 100);
    const handleScroll = () => enforceFixed();
    const handleResize = () => enforceFixed();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Use portal to render navbar directly to body
  return createPortal(
    <nav 
      id="main-navbar"
      className="fixed top-0 left-0 right-0 w-full"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        height: '64px',
        backgroundColor: 'rgba(29, 42, 59, 0.95)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(213, 181, 119, 0.2)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        zIndex: 9999,
        direction: language === 'ar' ? 'rtl' : 'ltr',
        visibility: 'visible',
        opacity: 1,
        display: 'block',
        transform: 'none',
        willChange: 'auto',
        contain: 'none',
        isolation: 'auto',
      }}
    >
      <div style={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        padding: '0 1rem',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem'
      }}>
        {/* Logo */}
        <Link 
          to="/" 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{ 
            display: 'flex', 
            alignItems: 'center',
            textDecoration: 'none'
          }}
        >
          <img 
            src="/hosta-global-logo-new.png?v=4" 
            alt="HOSTA GLOBAL" 
            style={{ 
              height: '48px',
              width: 'auto',
              maxWidth: '300px',
              objectFit: 'contain',
              borderRadius: '0.5rem'
            }}
          />
        </Link>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex items-center gap-5 flex-1 justify-center">
          {[
            { path: '/', key: 'home' },
            { path: '/properties', key: 'properties' },
            { path: '/exclusive-offers', key: 'exclusiveOffers' },
            { path: '/about', key: 'aboutUs' },
            { path: '/touring-guide', key: 'touringGuide' },
            { path: '/contact', key: 'contact' }
          ].map(({ path, key }) => (
            <Link
              key={path}
              to={path}
              style={{
                color: isActive(path) ? '#D5B577' : 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                padding: '0.5rem 0',
                position: 'relative',
                transition: 'color 0.3s',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => !isActive(path) && (e.target.style.color = '#D5B577')}
              onMouseLeave={(e) => !isActive(path) && (e.target.style.color = 'rgba(255, 255, 255, 0.8)')}
            >
              {t(`nav.${key}`)}
              {isActive(path) && (
                <span style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  backgroundColor: '#D5B577'
                }} />
              )}
            </Link>
          ))}
        </div>

        {/* Right Side: Language, Currency, Profile/Login */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.75rem'
        }}>
          {/* Language Selector - Desktop */}
          <div className="hidden md:block">
            <LanguageSelector />
          </div>

          {/* Currency Selector - Desktop */}
          <div className="hidden md:block">
            <CurrencySelector />
          </div>

          {/* Profile/Login - Desktop */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <div 
                ref={profileMenuRef}
                onMouseEnter={() => setIsProfileMenuOpen(true)}
                onMouseLeave={() => setIsProfileMenuOpen(false)}
                style={{ position: 'relative' }}
              >
                <button
                  onClick={() => navigate(getDashboardPath())}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(213, 181, 119, 0.2)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(213, 181, 119, 0.3)';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(213, 181, 119, 0.2)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <svg width="24" height="24" fill="none" stroke="#D5B577" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>

                {/* Profile Dropdown */}
                {isProfileMenuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 'calc(100% + 8px)',
                      width: '224px',
                      backgroundColor: 'rgba(29, 42, 59, 0.98)',
                      borderRadius: '0.75rem',
                      border: '1px solid rgba(213, 181, 119, 0.3)',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      zIndex: 50,
                      padding: '0.5rem 0'
                    }}
                    onMouseEnter={() => setIsProfileMenuOpen(true)}
                    onMouseLeave={() => setIsProfileMenuOpen(false)}
                  >
                    <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(213, 181, 119, 0.2)' }}>
                      <p style={{ color: 'white', fontSize: '0.875rem', fontWeight: 600, margin: 0 }}>{user.name}</p>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem', margin: '0.25rem 0 0 0' }}>{user.email}</p>
                    </div>
                    {[
                      { path: getDashboardPath(), key: 'dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                      { path: getDashboardPath(), key: 'myBookings', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                      { path: getDashboardPath(), key: 'savedProperties', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
                      { path: getDashboardPath(), key: 'profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
                      { path: getDashboardPath(), key: 'settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' }
                    ].map(({ path, key, icon }) => (
                      <button
                        key={key}
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          navigate(path, { state: { view: key === 'dashboard' ? 'dashboard' : key } });
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.625rem 1rem',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(213, 181, 119, 0.1)';
                          e.target.style.color = '#D5B577';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                        }}
                      >
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                        </svg>
                        {t(`nav.${key}`)}
                      </button>
                    ))}
                    <div style={{ borderTop: '1px solid rgba(213, 181, 119, 0.2)', margin: '0.25rem 0' }} />
                    <button
                      onClick={handleLogout}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '0.625rem 1rem',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                        e.target.style.color = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                      }}
                    >
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/register-owner"
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid rgba(213, 181, 119, 0.3)',
                    borderRadius: '0.5rem',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#D5B577';
                    e.target.style.borderColor = '#D5B577';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                  }}
                >
                  {t('nav.becomePartner')}
                </Link>
                {/* Phase: Login/Sign Up button hidden - uncomment to show
                <Link
                  to="/login"
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    backgroundColor: '#D5B577',
                    color: '#1D2A3B',
                    borderRadius: '0.5rem',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#c4a366'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#D5B577'}
                >
                  {t('nav.signIn')}
                </Link>
                */}
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2"
            aria-label="Toggle menu"
            style={{
              color: 'rgba(255, 255, 255, 0.8)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div 
          ref={mobileMenuRef}
          style={{
            borderTop: '1px solid rgba(213, 181, 119, 0.2)',
            backgroundColor: 'rgba(29, 42, 59, 0.98)',
            padding: '1rem',
            direction: language === 'ar' ? 'rtl' : 'ltr'
          }}
        >
          {[
            { path: '/', key: 'home' },
            { path: '/properties', key: 'properties' },
            { path: '/exclusive-offers', key: 'exclusiveOffers' },
            { path: '/about', key: 'aboutUs' },
            { path: '/touring-guide', key: 'touringGuide' },
            { path: '/contact', key: 'contact' }
          ].map(({ path, key }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setIsMenuOpen(false)}
              style={{
                display: 'block',
                padding: '0.625rem 0.5rem',
                color: isActive(path) ? '#D5B577' : 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                position: 'relative',
                marginBottom: '0.5rem'
              }}
            >
              {t(`nav.${key}`)}
              {isActive(path) && (
                <span style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '0.5rem',
                  right: '0.5rem',
                  height: '2px',
                  backgroundColor: '#D5B577'
                }} />
              )}
            </Link>
          ))}

          {/* Mobile Language Selector */}
          <div style={{ borderTop: '1px solid rgba(213, 181, 119, 0.2)', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.875rem', fontWeight: 500 }}>Language</span>
              <button
                onClick={() => {
                  const newLanguage = language === 'en' ? 'ar' : 'en';
                  setLanguage(newLanguage);
                  i18n.changeLanguage(newLanguage);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#D5B577',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <span style={{ color: language === 'en' ? '#D5B577' : 'rgba(255, 255, 255, 0.6)' }}>EN</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.3)', margin: '0 4px' }}>|</span>
                <span style={{ color: language === 'ar' ? '#D5B577' : 'rgba(255, 255, 255, 0.6)' }}>AR</span>
              </button>
            </div>
          </div>

          {/* Mobile Currency Selector */}
          <div style={{ borderTop: '1px solid rgba(213, 181, 119, 0.2)', paddingTop: '0.5rem' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>Currency</span>
            <CurrencySelector />
          </div>

          {/* Mobile User Section */}
          {user ? (
            <div style={{ borderTop: '1px solid rgba(213, 181, 119, 0.2)', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.875rem', margin: '0 0 0.25rem 0' }}>{user.name}</p>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem', margin: '0 0 0.75rem 0' }}>{user.email}</p>
              <Link
                to={getDashboardPath()}
                onClick={() => setIsMenuOpen(false)}
                style={{
                  display: 'block',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'rgba(213, 181, 119, 0.2)',
                  color: '#D5B577',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textAlign: 'center',
                  marginBottom: '0.5rem'
                }}
              >
                {t('nav.dashboard')}
              </Link>
              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#D5B577',
                  color: '#1D2A3B',
                  borderRadius: '0.5rem',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                {t('nav.logout')}
              </button>
            </div>
          ) : (
            <div style={{ borderTop: '1px solid rgba(213, 181, 119, 0.2)', paddingTop: '0.5rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Link
                to="/register-owner"
                onClick={() => setIsMenuOpen(false)}
                style={{
                  padding: '0.5rem 1rem',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(213, 181, 119, 0.3)',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textAlign: 'center'
                }}
              >
                {t('nav.becomePartner')}
              </Link>
              {/* Phase: Login/Sign Up button hidden - uncomment to show
              <Link
                to="/login"
                onClick={() => setIsMenuOpen(false)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#D5B577',
                  color: '#1D2A3B',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textAlign: 'center'
                }}
              >
                {t('nav.signIn')}
              </Link>
              */}
            </div>
          )}
        </div>
      )}
    </nav>,
    document.body
  );

  // Inject style tag with maximum specificity
  useEffect(() => {
    // Remove any existing style tag
    const existingStyle = document.getElementById('navbar-fixed-enforcer');
    if (existingStyle) existingStyle.remove();

    // Create and inject style tag
    const style = document.createElement('style');
    style.id = 'navbar-fixed-enforcer';
    style.textContent = `
      /* Maximum specificity - override everything */
      html body #main-navbar,
      html > body > #main-navbar,
      body#main-navbar,
      nav#main-navbar,
      #main-navbar[id="main-navbar"] {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        width: 100% !important;
        z-index: 9999 !important;
        transform: none !important;
        will-change: auto !important;
        contain: none !important;
        isolation: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        visibility: visible !important;
        opacity: 1 !important;
        display: block !important;
        bottom: auto !important;
      }
      
      /* Ensure body and html don't create containing blocks */
      html, body {
        position: static !important;
        transform: none !important;
        will-change: auto !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById('navbar-fixed-enforcer');
      if (styleToRemove) styleToRemove.remove();
    };
  }, []);
};

export default Navbar;
